import { useCallback, useMemo } from 'react';

import type { TimeFormat } from '@app/api/Branches/types';

import { useEmployeeColumn } from '@pages/Calendar/ui/EmployeeColumn/hooks/useEmployeeColumn';

import type { CalendarBreak, CalendarSlotMenu } from '@pages/Calendar/data';
import type { IndividualCalendarDay } from '@pages/IndividualCalendar/data';

interface UseDayColumnProps {
  day: IndividualCalendarDay;
  startTime: string;
  endTime: string;
  stepMinutes: number;
  intervalHeightPx: number;
  timeFormat?: TimeFormat | null;
  onSlotClick?: (date: string, time: string, endTime: string) => void;
  onSlotBreakClick?: (date: string, time: string, endTime: string) => void;
  onBreakClick?: (date: string, calendarBreak: CalendarBreak) => void;
  activeSlotMenu: CalendarSlotMenu | null;
  onSlotMenuOpen: (slotMenu: CalendarSlotMenu) => void;
  onSlotMenuClose: () => void;
}

export const useDayColumn = ({
  day,
  startTime,
  endTime,
  stepMinutes,
  intervalHeightPx,
  timeFormat,
  onSlotClick,
  onSlotBreakClick,
  onBreakClick,
  activeSlotMenu,
  onSlotMenuOpen,
  onSlotMenuClose,
}: UseDayColumnProps) => {
  const dateKey = useMemo(() => day.date, [day.date]);
  const isExecutorNonWorkingDay = useMemo(
    () => day.isExecutorNonWorkingDay,
    [day.isExecutorNonWorkingDay]
  );

  const slotClickHandler = useCallback(
    (dayKey: string, time: string, endTime: string) => {
      onSlotClick?.(dayKey, time, endTime);
    },
    [onSlotClick]
  );

  const slotBreakClickHandler = useCallback(
    (dayKey: string, time: string, endTime: string) => {
      onSlotBreakClick?.(dayKey, time, endTime);
    },
    [onSlotBreakClick]
  );

  const breakClickHandler = useCallback(
    (_dayKey: string, calendarBreak: CalendarBreak) => {
      onBreakClick?.(dateKey, calendarBreak);
    },
    [dateKey, onBreakClick]
  );

  const {
    timeSlots,
    layoutedEvents,
    breakBlocks,
    activeEventId,
    gridHoverHandlers,
    handleBreakClick,
    handleSlotClick,
    slotMenu,
    slotMenuItems,
    closeSlotMenu,
  } = useEmployeeColumn({
    ...day.column,
    events: day.events,
    startTime,
    endTime,
    stepMinutes,
    intervalHeightPx,
    timeFormat,
    onSlotClick: slotClickHandler,
    onSlotBreakClick: slotBreakClickHandler,
    onBreakClick: breakClickHandler,
    activeSlotMenu,
    onSlotMenuOpen,
    onSlotMenuClose,
  });

  return {
    dateKey,
    isExecutorNonWorkingDay,
    timeSlots,
    layoutedEvents,
    breakBlocks,
    activeEventId,
    gridHoverHandlers,
    handleBreakClick,
    handleSlotClick,
    slotMenu,
    slotMenuItems,
    closeSlotMenu,
  };
};
